import { Alert, AlertTitle, Box } from "@mui/material"
import { WarningOutlined } from "@mui-symbols-material/w300"
import type { FC } from "react"

import type { Contact } from "@/graphql/codegen/graphql"
import { useInfiniteScroll } from "@/hooks/useInfniniteScroll"
import ContactCard from "@/screens/Companies/edit/components/contacts/ContactCard"
import { DEFAULT_PAGE_SIZE } from "@/stores/createSearchStore"

interface ContactsCardListProps {
  fetching: boolean
  data: Contact[]
  setIsCommentDialogOpen: (open: boolean) => void
  setSelectedContact: (contact: Contact) => void
  setIsLocationRolesDialogOpen: (open: boolean) => void
  setIsSubscriptionsDialogOpen: (open: boolean) => void
  onContactClick: (contact: Contact) => void
  throttledLoadMore: () => void
}

const ContactsCardList: FC<ContactsCardListProps> = ({
  throttledLoadMore,
  fetching,
  data,
  setSelectedContact,
  setIsCommentDialogOpen,
  setIsSubscriptionsDialogOpen,
  setIsLocationRolesDialogOpen,
  onContactClick,
}) => {
  const { containerRef } = useInfiniteScroll(throttledLoadMore, {
    isLoading: fetching,
    hasMore: data.length % DEFAULT_PAGE_SIZE === 0,
    threshold: 200,
  })

  if (data.length === 0 && !fetching) {
    return (
      <Alert
        icon={<WarningOutlined />}
        severity='info'
        variant='filled'
        classes={{
          message: "w-full flex flex-row justify-between",
        }}
      >
        <Box>
          <AlertTitle className='font-normal leading-7'>No contacts found</AlertTitle>
        </Box>
      </Alert>
    )
  }

  return (
    <Box className='grow overflow-y-auto' ref={containerRef}>
      <Box className='grid w-full grid-cols-[repeat(auto-fill,_minmax(321px,_1fr))] gap-6 p-4'>
        {data.map((contact: Contact) => (
          <ContactCard
            key={contact.contactId}
            contact={contact}
            onCardClick={() => onContactClick(contact)}
            onCommentClick={() => {
              setIsCommentDialogOpen(true)
              setSelectedContact(contact)
            }}
            onSubscriptionClick={() => {
              setSelectedContact(contact)
              setIsSubscriptionsDialogOpen(true)
            }}
            onLocationClick={() => {
              setSelectedContact(contact)
              setIsLocationRolesDialogOpen(true)
            }}
          />
        ))}
      </Box>
    </Box>
  )
}

export default ContactsCardList
